import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Zap, ArrowRight, Check, Sparkles, ArrowLeft, Activity } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const COACHING_TYPES = [
  { id: 'private', label: 'Private training', sub: 'Independent / 1-on-1' },
  { id: 'club', label: 'Club coaching', sub: 'Inside an academy' },
  { id: 'both', label: 'Both', sub: 'Private + club coaching' },
];

const NUM_ATHLETES = ['1-5', '6-15', '16-30', '30+'];

const CoachSignupPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { coachSignup } = useAuth();

  // V1: Performance Coach signup mode — entered via ?role=performance from
  // /get-started. Locks coach_role=private_performance and hides irrelevant
  // soccer-specific onboarding fields (coaching_type / num_athletes).
  const isPerformance = searchParams.get('role') === 'performance';
  const coachRole = isPerformance ? 'private_performance' : 'private_soccer';

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    business_name: '',
    sport: 'soccer',
    plan: 'pro',                  // Founding Beta — single Pro experience
    coach_role: coachRole,
    coaching_type: isPerformance ? 'private' : '',  // pre-filled for performance flow
    num_athletes: isPerformance ? '1-5' : '',       // pre-filled for performance flow
  });

  const set = (k, v) => setFormData((f) => ({ ...f, [k]: v }));

  const canSubmit =
    formData.full_name.trim().length >= 2 &&
    /^\S+@\S+\.\S+$/.test(formData.email) &&
    formData.password.length >= 6 &&
    formData.business_name.trim().length >= 2 &&
    formData.coaching_type &&
    formData.num_athletes;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    try {
      await coachSignup(formData);
      toast.success(isPerformance
        ? 'Welcome — Performance Coach access granted'
        : 'Welcome to Meybra — Founding Beta access granted');
      navigate('/coach');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Signup failed');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#050505] flex">
      <div className="absolute inset-0 green-mesh opacity-20" />

      {/* Left Panel - Info */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-center px-12 xl:px-20 relative z-10">
        <Link to="/" className="flex items-center gap-2 mb-12">
          <Zap className="text-primary fill-primary" size={32} strokeWidth={2.5} />
          <span className="font-heading text-3xl tracking-wider">MEYBRA</span>
        </Link>

        <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/30 rounded-full px-3 py-1 mb-6 w-fit">
          <Sparkles size={11} className="text-primary" />
          <span className="text-[10px] uppercase tracking-[0.25em] text-primary">Founding Beta Access</span>
        </div>

        <h1 className="font-heading text-5xl uppercase tracking-tight mb-6 leading-tight">
          Build your coaching<br />
          <span className="text-primary">methodology</span>
        </h1>

        <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
          Complimentary access during the validation phase. No credit card required. Set up in minutes.
        </p>

        <ul className="space-y-4">
          {[
            'Manage athletes & training plans',
            'Build your drill library (60+ included)',
            'Video analysis & parent communication',
            'White-label your brand (Pro)',
          ].map((item, idx) => (
            <li key={idx} className="flex items-center gap-3">
              <Check className="text-primary" size={18} />
              <span className="text-muted-foreground">{item}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Right Panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <Link
            to="/get-started"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors mb-6"
            data-testid="coach-signup-back"
          >
            <ArrowLeft size={13} /> Back
          </Link>

          <p className="text-[10px] uppercase tracking-[0.25em] text-primary/70 mb-2" data-testid="coach-signup-mode-tag">
            {isPerformance ? 'For Performance Specialists' : 'For Coaches'}
          </p>
          <h2 className="font-heading text-2xl sm:text-3xl uppercase tracking-wide mb-2" data-testid="coach-signup-title">
            {isPerformance ? 'Create your Performance Coach account' : 'Create your coach account'}
          </h2>
          <p className="text-sm text-muted-foreground mb-8">
            {isPerformance
              ? 'Physical specialist mode — speed, agility, explosiveness, endurance. Your evaluations feed the unified athlete profile.'
              : 'Just a few essentials — you can always add more later from your settings.'}
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 gap-4">
              <div>
                <Label htmlFor="coach-full-name" className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">Full Name *</Label>
                <Input
                  id="coach-full-name"
                  value={formData.full_name}
                  onChange={(e) => set('full_name', e.target.value)}
                  placeholder="Your name"
                  className="bg-zinc-900/60 border-border/30"
                  data-testid="coach-signup-name"
                  required
                />
              </div>
              <div>
                <Label htmlFor="coach-business" className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">Coaching Business Name *</Label>
                <Input
                  id="coach-business"
                  value={formData.business_name}
                  onChange={(e) => set('business_name', e.target.value)}
                  placeholder="e.g. Silva Football Training"
                  className="bg-zinc-900/60 border-border/30"
                  data-testid="coach-signup-business"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="coach-email" className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">Email *</Label>
                  <Input
                    id="coach-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => set('email', e.target.value)}
                    placeholder="you@example.com"
                    className="bg-zinc-900/60 border-border/30"
                    data-testid="coach-signup-email"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="coach-password" className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">Password *</Label>
                  <Input
                    id="coach-password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => set('password', e.target.value)}
                    placeholder="Min 6 characters"
                    className="bg-zinc-900/60 border-border/30"
                    data-testid="coach-signup-password"
                    minLength={6}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Coaching Type — hidden for Performance Coach (irrelevant to physical specialists) */}
            {!isPerformance && (
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">Coaching Type *</Label>
                <div className="grid grid-cols-3 gap-2">
                  {COACHING_TYPES.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => set('coaching_type', t.id)}
                      className={`p-3 rounded-sm border text-left transition-all ${
                        formData.coaching_type === t.id
                          ? 'bg-primary/15 border-primary/50 text-primary'
                          : 'bg-zinc-900/60 border-border/30 text-muted-foreground hover:border-primary/30'
                      }`}
                      data-testid={`coach-signup-type-${t.id}`}
                    >
                      <p className="text-xs font-semibold">{t.label}</p>
                      <p className="text-[10px] mt-0.5 opacity-70">{t.sub}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Number of Athletes — hidden for Performance Coach (athletes invite them 1-by-1) */}
            {!isPerformance && (
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">How many athletes do you coach today? *</Label>
                <div className="grid grid-cols-4 gap-2">
                  {NUM_ATHLETES.map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => set('num_athletes', n)}
                      className={`py-2.5 rounded-sm text-sm font-semibold transition-all ${
                        formData.num_athletes === n
                          ? 'bg-primary text-black'
                          : 'bg-zinc-900/60 border border-border/30 text-muted-foreground hover:border-primary/30'
                      }`}
                      data-testid={`coach-signup-num-${n}`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Performance Coach disclosure — explain restricted scope */}
            {isPerformance && (
              <div className="rounded-sm border border-orange-400/30 bg-orange-400/5 p-4" data-testid="performance-coach-disclosure">
                <div className="flex items-start gap-3">
                  <Activity size={18} className="text-orange-400 shrink-0 mt-0.5" />
                  <div className="text-xs leading-relaxed text-muted-foreground">
                    <p className="text-orange-300 font-semibold uppercase tracking-wider text-[10px] mb-1.5">Performance Mode</p>
                    You will only evaluate <span className="text-foreground font-medium">physical attributes</span> (speed, agility, explosiveness, endurance, balance, coordination, acceleration). Technical and tactical evaluations are reserved for Soccer Coaches.
                  </div>
                </div>
              </div>
            )}

            <Button
              type="submit"
              disabled={!canSubmit || loading}
              className="w-full bg-primary text-black hover:bg-primary/90 uppercase tracking-wider text-sm font-bold py-3 h-auto disabled:opacity-40"
              data-testid="coach-signup-submit"
            >
              {loading ? 'Creating Account...' : 'Join Founding Beta'}
              {!loading && <ArrowRight size={14} className="ml-2" />}
            </Button>

            <p className="text-[11px] text-muted-foreground text-center">
              Already have an account?{' '}
              <Link to="/unified-login" className="text-primary hover:underline">Sign in</Link>
            </p>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default CoachSignupPage;
